/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useEffect } from 'react';
import { TextField } from '@mui/material';
import { useFormik } from 'formik';
import { toNumber } from 'lodash';
import isNil from 'lodash/isNil';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { fetchTickets } from '../../store/tickets/actions';
import { TicketsState } from '../../store/tickets/reducer';
import StyledProps from '../common/theme/AppThemeInterface';
import { TicketsTablePageNumberInputType } from './TicketsTablePaginationControlsPageJumpFormNumberFormatCustom';
import TicketsTablePaginationControlsPageJumpFormNumberFormatCustom from './TicketsTablePaginationControlsPageJumpFormNumberFormatCustom';

// Formik function for page jump input
export interface TicketsTablPaginationControlsPageJumpFormProps {
  tickets: Readonly<TicketsState>;
}

const TicketsTablPaginationControlsPageJumpForm = (props: TicketsTablPaginationControlsPageJumpFormProps) => {
  const dispatch = useDispatch();
  const { tickets } = props;

  // Pass the useFormik() hook initial form values and a submit function that will
  // be called when the form is submitted
  const formik = useFormik({
    initialValues: {
      page: (tickets.options || {}).page || 0 + 1,
    },
    onSubmit: values => {
      const page = values.page;
      if (toNumber(page) && !isNil(page)) {
        if ((tickets.nextOptions || {}).page !== toNumber(page)) {
          dispatch(fetchTickets({ ...tickets.options, page: toNumber(page) - 1 }));
        }
      }
    },
  });

  const handleFormInputChange = (event: TicketsTablePageNumberInputType) => {
    if (tickets.options && !isNil(tickets.options.page)) {
      if (event.target.value !== '' && toNumber(event.target.value) !== formik.values.page) {
        dispatch(fetchTickets({ ...tickets.options, page: toNumber(event.target.value) - 1 }));
      }
    }
  };

  useEffect(() => {
    if (tickets.options && !isNil(tickets.options.page)) {
      if (formik.getFieldProps('page').value !== tickets.options.page + 1) {
        formik.setFieldValue('page', tickets.options.page + 1);
      }
    }
  }, [tickets, formik]);

  if (!tickets.options) {
    return <></>;
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <StyledPaginationTextField
        fullWidth
        id="page"
        name="page"
        size="small"
        type="text"
        variant="outlined"
        autoComplete="off"
        value={formik.values.page}
        InputProps={{
          autoComplete: 'off',
          form: {
            autoComplete: 'off',
          },
          inputComponent: TicketsTablePaginationControlsPageJumpFormNumberFormatCustom as any,
        }}
        onChange={handleFormInputChange}
      />
    </form>
  );
};

export default TicketsTablPaginationControlsPageJumpForm;

const StyledPaginationTextField = styled(TextField)`
  background-color: ${({ theme }: StyledProps) => theme.secondaryBackgroundColor};
  font-family: ${({ theme }: StyledProps) => theme.appFontFamily};
  div {
    input {
      color: ${({ theme }: StyledProps) => theme.primaryTextColor};
    }
  }
`;
